import React from "react";

import { Helmet } from "react-helmet";

import {
  ICosignMappedProperty,
  ICosignPropertyMappings,
  ICosignUnmappedProperty,
} from "manager/hooks/api/integrationQueries";
import Container from "shared/components/Container";
import Icon from "shared/components/Icon";
import { BackLink } from "shared/components/Links";
import PageTitle from "shared/components/PageTitle";
import Skeleton from "shared/components/Skeleton";
import { BackLinkProps } from "shared/interfaces/misc";

import CosignMappedPropertiesTable from "./CosignMappedPropertiesTable";
import CosignPmUuidSummary from "./CosignPmUuidSummary";
import CosignUnmappedPropertiesTable from "./CosignUnmappedPropertiesTable";
import {
  CosignSummaryItem,
  CosignPmUuidSummaryItem,
  CosignSummaryLabel,
  CosignSummaryRow,
  CosignSummaryValue,
  CosignTitleContainer,
  DashboardError,
  DashboardWarningBanner,
} from "./styled";

export interface CosignDashboardProps {
  backLink?: BackLinkProps;
  mappings?: ICosignPropertyMappings;
  isLoading: boolean;
  hasError: boolean;
  onRetry: () => void;
  unmappedSearchTerm: string;
  onUnmappedSearchChange: (value: string) => void;
  filteredUnmappedProperties: ICosignUnmappedProperty[];
  onDisconnect: (property: ICosignMappedProperty) => void;
  onToggle: (property: ICosignMappedProperty, isActive: boolean) => void;
  isToggleUpdating?: boolean;
  onMapToExisting: (property: ICosignUnmappedProperty) => void;
  onCreateInCosign: (property: ICosignUnmappedProperty) => void;
  createModal: React.ReactNode;
}

const title = "Dashboard";

/**
 * Cosign property mapping dashboard (PRODUCT-988).
 * Renders mapped and unmapped property sections with loading and error states.
 */
const CosignDashboard: React.FC<CosignDashboardProps> = ({
  backLink,
  mappings,
  isLoading,
  hasError,
  onRetry,
  unmappedSearchTerm,
  onUnmappedSearchChange,
  filteredUnmappedProperties,
  onDisconnect,
  onToggle,
  isToggleUpdating,
  onMapToExisting,
  onCreateInCosign,
  createModal,
}) => (
  <div>
    <Container expand noPaddingBottom>
      <Helmet>
        <title>{`Cosign ${title}`}</title>
      </Helmet>
      {backLink && (
        <BackLink.Container>
          <BackLink {...backLink} />
        </BackLink.Container>
      )}
      <PageTitle.Container center>
        <CosignTitleContainer>
          <Icon.CosignPersonIcon width={120} height={28} />
          {title}
        </CosignTitleContainer>
      </PageTitle.Container>
    </Container>

    <Container>
      <Skeleton loading={isLoading && !mappings}>
        {hasError ? (
          <DashboardError data-testid="cosign-dashboard-error" role="alert">
            We couldn&apos;t load Cosign property mappings.{" "}
            <button type="button" onClick={onRetry}>
              Try again
            </button>
          </DashboardError>
        ) : (
          <>
            {(mappings?.pmStatus || "").toLowerCase() === "disabled" && (
              <DashboardWarningBanner
                data-testid="cosign-pm-disabled-banner"
                role="alert"
              >
                <Icon.Info />
                <span>
                  <strong>This Cosign account has been disabled.</strong>
                  Referrals are paused until it is reactivated. Contact Cosign
                  support to restore access.
                </span>
              </DashboardWarningBanner>
            )}

            <CosignSummaryRow data-testid="cosign-dashboard-summary">
              <CosignPmUuidSummaryItem>
                <CosignPmUuidSummary
                  maskedPropertyManagerUuid={mappings?.maskedPropertyManagerUuid}
                  propertyManagerUuid={mappings?.propertyManagerUuid}
                />
              </CosignPmUuidSummaryItem>
              <CosignSummaryItem>
                <CosignSummaryLabel>Account status</CosignSummaryLabel>
                <CosignSummaryValue data-testid="cosign-dashboard-pm-status">
                  {mappings?.pmStatus || "—"}
                </CosignSummaryValue>
              </CosignSummaryItem>
              <CosignSummaryItem>
                <CosignSummaryLabel>Cosign enabled</CosignSummaryLabel>
                <CosignSummaryValue data-testid="cosign-enabled-count">
                  {mappings?.cosignEnabledCount ?? 0}
                </CosignSummaryValue>
              </CosignSummaryItem>
              <CosignSummaryItem>
                <CosignSummaryLabel>Other VERO properties</CosignSummaryLabel>
                <CosignSummaryValue data-testid="cosign-unmapped-count">
                  {mappings?.otherVeroCount ?? 0}
                </CosignSummaryValue>
              </CosignSummaryItem>
            </CosignSummaryRow>

            <CosignMappedPropertiesTable
              properties={mappings?.cosignEnabledProperties ?? []}
              isLoading={isLoading}
              onDisconnect={onDisconnect}
              onToggle={onToggle}
              isToggleUpdating={isToggleUpdating}
            />

            <CosignUnmappedPropertiesTable
              properties={filteredUnmappedProperties}
              totalUnmappedCount={mappings?.otherVeroProperties?.length ?? 0}
              isLoading={isLoading}
              searchTerm={unmappedSearchTerm}
              onSearchChange={onUnmappedSearchChange}
              onMapToExisting={onMapToExisting}
              onCreateInCosign={onCreateInCosign}
            />
          </>
        )}
      </Skeleton>
    </Container>

    {createModal}
  </div>
);

export default CosignDashboard;
